import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { locField } from "@/lib/i18nField";
import { formatPrice } from "@/lib/price";
import PostCard from "@/components/community/PostCard";
import CreatePostDialog from "@/components/community/CreatePostDialog";
import { Users, Plus, MapPin, CalendarDays, Sparkles, MessageCircle, ArrowRight, Tag } from "lucide-react";

const CANTONS = ["VD", "GE", "VS", "FR", "NE", "JU", "BE", "ZH", "LU", "SG", "TI", "BS", "BL", "AG", "GR"];
const LANGS = [["all", "com_lang_all"], ["fr", "Français"], ["de", "Deutsch"], ["it", "Italiano"], ["en", "English"]];

export default function Community({ initialTab = "foryou" }) {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("recent");
  const [flang, setFlang] = useState("all");
  const [canton, setCanton] = useState("");
  const [dialog, setDialog] = useState(false);

  const { data: categories = [] } = useQuery({ queryKey: ["comCats"], queryFn: async () => (await api.get("/community/categories")).data });
  const { data: feed, refetch: refetchFeed } = useQuery({ queryKey: ["comFeed"], queryFn: async () => (await api.get("/community/feed")).data, enabled: tab === "foryou" });
  const { data: list, refetch: refetchList } = useQuery({ queryKey: ["comPosts", category, sort, flang], queryFn: async () => (await api.get("/community/posts", { params: { category: category || undefined, sort, lang: flang, limit: 30 } })).data, enabled: tab === "discussions" });
  const { data: near } = useQuery({ queryKey: ["comNear", canton], queryFn: async () => (await api.get("/community/posts", { params: { cantons: canton || undefined, limit: 30 } })).data, enabled: tab === "near" });
  const { data: events = [] } = useQuery({ queryKey: ["comEvents"], queryFn: async () => { const r = (await api.get("/events")).data; return Array.isArray(r) ? r : (r.events || r.items || []); }, enabled: tab === "events" });
  const { data: mine, refetch: refetchMine } = useQuery({ queryKey: ["comMine"], queryFn: async () => (await api.get("/community/me")).data, enabled: tab === "mine" && !!user });
  const { data: listingsPreview } = useQuery({ queryKey: ["comListingsPreview"], queryFn: async () => (await api.get("/community/listings", { params: { status: "available", limit: 6 } })).data, enabled: tab === "foryou" });

  const tabs = [["foryou", Sparkles], ["discussions", MessageCircle], ["near", MapPin], ["events", CalendarDays], ["annonces", Tag], ["members", Users], ["mine", Users]];
  const onCreated = () => { refetchFeed?.(); refetchList?.(); refetchMine?.(); };

  const PostList = ({ posts }) => (
    posts?.length ? <div className="space-y-3">{posts.map((p) => <PostCard key={p.id} post={p} />)}</div>
      : <div className="text-center py-16 text-stone-500 border border-dashed border-[#272A34] rounded-lg" data-testid="community-empty">{t("com_no_posts")}</div>
  );

  return (
    <>
      <Seo title={t("com_title")} description={t("com_subtitle")} canonicalPath="/community" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
        <div className="mb-6 border-b border-amber-900/30 pb-5">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> swiss-pagan.ch</div>
          <h1 className="font-serif text-4xl sm:text-5xl uppercase tracking-tight text-amber-100">{t("com_title")}</h1>
          <p className="text-stone-400 mt-3 max-w-2xl">{t("com_subtitle")}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1 -mx-1 px-1">
          {tabs.map(([k, Icon]) => (
            <button key={k} data-testid={`community-tab-${k}`} onClick={() => k === "members" ? navigate("/community/members") : k === "annonces" ? navigate("/community/annonces") : setTab(k)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all ${tab === k ? "bg-amber-600 text-stone-950 font-semibold" : "bg-[#121418] text-stone-300 border border-[#272A34] hover:border-amber-500/40"}`}>
              <Icon className="w-4 h-4" /> {t(`com_tab_${k}`)}
            </button>
          ))}
        </div>

        {tab === "foryou" && (
          <>
            <section className="mb-8" data-testid="community-listings-preview">
              <div className="flex items-end justify-between mb-3">
                <h2 className="font-serif text-xl text-stone-100 flex items-center gap-2"><Tag className="w-4 h-4 text-amber-500" /> {t("com_listings_title")}</h2>
                <Link to="/community/annonces" data-testid="community-listings-all" className="font-mono text-xs uppercase tracking-wider text-amber-500 hover:text-amber-400 flex items-center gap-1.5">{t("com_listings_all")} <ArrowRight className="w-3.5 h-3.5" /></Link>
              </div>
              {listingsPreview?.listings?.length > 0 ? (
                <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
                  {listingsPreview.listings.map((l) => (
                    <Link key={l.id} to={`/community/annonces/${l.id}`} data-testid={`community-listing-preview-${l.id}`} className="group shrink-0 w-44 bg-[#121418] border border-[#272A34] hover:border-amber-500/50 rounded-xl overflow-hidden transition-all hover:-translate-y-1">
                      <div className="relative h-28 bg-[#0B0C0E]">
                        {l.photos?.[0] ? <img src={l.photos[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Tag className="w-6 h-6 text-stone-700" /></div>}
                        <span className={`absolute top-1.5 left-1.5 text-[9px] font-mono uppercase px-1.5 py-0.5 rounded ${l.kind === "search" ? "bg-indigo-600/90 text-white" : "bg-amber-600/90 text-stone-950"}`}>{l.kind === "search" ? t("lst_kind_search") : t("lst_kind_sell")}</span>
                      </div>
                      <div className="p-3">
                        <h3 className="font-serif text-sm text-amber-100 group-hover:text-amber-50 leading-tight line-clamp-2">{l.title}</h3>
                        <div className="mt-1 font-serif text-sm text-amber-400">{l.kind === "sell" ? (l.price_cents != null ? formatPrice(l.price_cents, l.currency) : t("lst_none_price")) : t("lst_looking_for")}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <Link to="/community/annonces" data-testid="community-listings-empty" className="block text-center py-8 text-stone-500 border border-dashed border-[#272A34] rounded-lg text-sm hover:border-amber-500/40 transition-colors">{t("com_listings_empty_cta")}</Link>
              )}
            </section>
            <PostList posts={feed?.posts} />
          </>
        )}

        {tab === "discussions" && (
          <>
            <div className="flex flex-wrap gap-2 mb-5">
              <button onClick={() => setCategory("")} className={`px-3 py-1.5 rounded-full text-xs ${!category ? "bg-amber-700 text-stone-950" : "bg-[#121418] text-stone-300 border border-[#272A34]"}`} data-testid="cat-filter-all">{t("com_all_cats")}</button>
              {categories.map((c) => (
                <button key={c.key} data-testid={`cat-filter-${c.key}`} onClick={() => setCategory(c.key)} className={`px-3 py-1.5 rounded-full text-xs ${category === c.key ? "bg-amber-700 text-stone-950" : "bg-[#121418] text-stone-300 border border-[#272A34] hover:border-amber-500/40"}`}>{locField(c.translations?.name, lang)}</button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mb-5">
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select" className="bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-1.5 text-xs text-stone-300">
                <option value="recent">{t("com_recent")}</option><option value="popular">{t("com_popular")}</option>
              </select>
              <select value={flang} onChange={(e) => setFlang(e.target.value)} data-testid="lang-select" className="bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-1.5 text-xs text-stone-300">
                {LANGS.map(([v, l]) => <option key={v} value={v}>{l.startsWith("com_") ? t(l) : l}</option>)}
              </select>
            </div>
            {!category && (
              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                {categories.map((c) => (
                  <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`cat-card-${c.key}`} className="text-left bg-[#121418] border border-[#272A34] hover:border-amber-500/40 rounded-lg p-4 transition-all">
                    <h3 className="font-serif text-stone-100">{locField(c.translations?.name, lang)}</h3>
                    <p className="text-xs text-stone-500 line-clamp-2 mt-1">{locField(c.translations?.description, lang)}</p>
                    <span className="text-[11px] font-mono text-amber-600/80 mt-2 inline-block">{c.post_count} · {t("com_tab_discussions")}</span>
                  </button>
                ))}
              </div>
            )}
            <PostList posts={list?.posts} />
          </>
        )}

        {tab === "near" && (
          <>
            <p className="text-sm text-stone-400 mb-4">{t("com_near_intro")}</p>
            <div className="flex flex-wrap gap-2 mb-5">
              <button onClick={() => setCanton("")} data-testid="canton-all" className={`px-3 py-1.5 rounded-full text-xs ${!canton ? "bg-amber-700 text-stone-950" : "bg-[#121418] text-stone-300 border border-[#272A34]"}`}>{t("com_near_all_cantons")}</button>
              {CANTONS.map((c) => <button key={c} data-testid={`canton-${c}`} onClick={() => setCanton(c)} className={`px-3 py-1.5 rounded-full text-xs font-mono ${canton === c ? "bg-amber-700 text-stone-950" : "bg-[#121418] text-stone-300 border border-[#272A34] hover:border-amber-500/40"}`}>{c}</button>)}
            </div>
            <PostList posts={near?.posts} />
          </>
        )}

        {tab === "events" && (
          <div className="space-y-3">
            {events.slice(0, 30).map((e) => (
              <Link key={e.slug} to={`/events/${e.slug}#discussion`} data-testid={`com-event-${e.slug}`} className="flex items-center gap-4 bg-[#121418] border border-[#272A34] hover:border-amber-500/40 rounded-lg p-3 transition-all">
                {e.image && <img src={e.image} alt="" className="w-16 h-16 rounded-md object-cover shrink-0" />}
                <div className="flex-1 min-w-0"><h3 className="font-serif text-stone-100 truncate">{e.title}</h3><p className="text-xs text-stone-500 truncate">{e.city}{e.canton ? ` · ${e.canton}` : ""}</p></div>
                <span className="inline-flex items-center gap-1 text-xs text-amber-500 shrink-0">{t("com_event_see_discussion")} <ArrowRight className="w-4 h-4" /></span>
              </Link>
            ))}
          </div>
        )}

        {tab === "mine" && (
          !user ? <div className="text-center py-16 text-stone-400 border border-dashed border-[#272A34] rounded-lg">{t("com_login_required")}</div>
            : (
              <div className="space-y-8">
                <div><h2 className="font-serif text-xl text-stone-100 mb-3">{t("com_me_posts")}</h2><PostList posts={mine?.posts} /></div>
                <div><h2 className="font-serif text-xl text-stone-100 mb-3">{t("com_me_followed")}</h2><PostList posts={mine?.followed} /></div>
                <Link to="/notifications" className="inline-block text-amber-500 hover:text-amber-400 text-sm">{t("nav_notifications")} →</Link>
              </div>
            )
        )}
      </div>

      {/* Floating create button */}
      {user && (
        <button data-testid="community-create-fab" onClick={() => setDialog(true)}
          className="fixed bottom-6 right-6 z-[1150] inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-5 py-3 rounded-full shadow-xl">
          <Plus className="w-5 h-5" /> <span className="hidden sm:inline">{t("com_create")}</span>
        </button>
      )}
      <CreatePostDialog open={dialog} onClose={() => setDialog(false)} categories={categories} defaultCategory={category} onCreated={onCreated} />
    </>
  );
}
